import { PaginatedResult } from "../endpoints/response/paginated_result";
import { Connection, ConnectionSession } from "../entities/connection";
import { Directory } from "../entities/directory";
import { HealthTestResult, HealthTestType } from "../entities/health_status";
import { IHour, IHourPost, WeekDayNames } from "../entities/hours";
import { Invitation, PendingInvitation } from "../entities/invitation";
import { LeavePolicyType } from "../entities/leave";
import { INews } from "../entities/news";
import { Office } from "../entities/office";
import { Organization } from "../entities/organization";
import { PresenceType } from "../entities/presence";
import { IQuestion, IQuestionItem } from "../entities/question";
import { IQuestionnaire } from "../entities/questionnaire";
import {
  IResource,
  ResourceImageVariants,
  ResourceType,
  TreeResources
} from "../entities/resource";
import {
  AssignmentGroupType,
  IResourcePermission
} from "../entities/resource_permission";
import {
  AvailabilityErrorCode,
  AvailabilityErrorDetails,
  ISchedule
} from "../entities/schedule";
import { IStation } from "../entities/station";
import type { WorkplaceRequestStatus } from "./workplace-requests";

export type { ITeam } from "../entities/team";

import { Application, ApplicationWithKeys } from "../entities/application";
import {
  NewsAdded,
  Notification,
  ScheduleResponded
} from "../entities/notification";
import { ITeamData } from "../entities/team";
import { IUserFull } from "../entities/user";
import { IUserResourceManaged } from "../entities/user_resource";
import {
  IDocumentPartial,
  ITestDocument,
  ITestDocumentPartial,
  IVaccinationDocument,
  IVaccinationDocumentPartial,
  IVaccine
} from "../entities/vaccine";
import { IVisit } from "../entities/visit";
import {
  WifiAppDownload,
  WifiAppDownloadType,
  WifiAppInfo,
  WifiConnection
} from "../entities/wifi";

export {
  type CalendarAuthorizationCreateOrUpdateRequest,
  type CalendarAuthorizationIntentCreateOrUpdateRequest,
  type CalendarCreateSyncJobRequest,
  type CalendarEventSyncRequest,
  type CalendarIntegrationSyncRequest,
  type CalendarLeaveSyncRequest,
  type CalendarListenerListeningResponse,
  type CalendarScheduleSyncRequest,
  CalendarSource,
  type CalendarSyncRequest,
  CalendarType,
  type ICalendar,
  type ICalendarAuthorization,
  type ICalendarAuthorizationForSyncing,
  type ICalendarAuthorizationIntent,
  type ICalendarAuthorizationIntentForSyncing,
  type ICalendarEvent,
  type ICalendarListener,
  type ICalendarPendingEvent,
  type ICalendarSyncCheck,
  type ICalendarSyncJob,
  type IPendingCalendarEvent,
  type IPendingCalendarSyncJob,
  type ResGetCalendarSyncJobs,
  type ResGetCalendars
} from "../entities/calendar";
export type { PaginationOptions } from "../entities/pagination_options";
export type { UserRoles } from "../entities/user";
export {
  type IUserSettings,
  type ResUserSettings,
  type ResUserSettingsCore,
  SAVED_VIEW_LIMITS,
  type SavedView,
  type SectionViews,
  UserPropertyType
} from "../entities/user_settings";
export { GoogleCalendarTransformer } from "../integrations/google";
export { MicrosoftCalendarTransformer } from "../integrations/microsoft";

export type ResGetOrganizations = PaginatedResult<Organization>;
export type ResGetOrganization = Organization;

export interface BodyPostOrganizations {
  slug: string;
  name: string;
}

export interface ResPutInvitationsJoin {
  id: string;
  slug: string;
  name: string;
}

export interface ResPostUsersImpersonate {
  token: string;
}

export type IOfficeUsageFormatted = {
  previousWeekDate: string;
  month: string;
  days: { [p: string]: WeekDayNames };
  usages: {
    date: string;
    total: number;
    occupancy: string | number;
    accepted: number;
    free: number;
    day: string;
  }[];
  weekDate: string;
  nextWeekDate: string;
};

export interface ResPostUsers {
  id: string;
  access_token: string;
}

export type ResGetInvitation = Invitation;

export type ResGetPendingInvitation = PendingInvitation;

export type ResGetInvitations = PaginatedResult<ResGetInvitation>;

export type ResGetPendingInvitations = PaginatedResult<ResGetPendingInvitation>;

export type ResGetOfficesQuestionnaire = IQuestionnaire;

export type ResGetQuestions = PaginatedResult<IQuestionItem>;
export type ResGetQuestion = IQuestion;

export type ResGetOfficesNews = PaginatedResult<INews>;

export interface ResGetOfficesResources {
  cursor: string;
  byId: {
    [id: string]: IResource;
  };
  elements: IResource[];
  non_available?: {
    resource: IResource;
    reason_code: AvailabilityErrorCode;
    details?: AvailabilityErrorDetails;
    reason: string;
  }[];
  options: IGenericOption[];
  tree: TreeResources[];
}

export type ResGetResource = IResource;

export const WL_PRESENCE_OFFICE = "office";
export const WL_PRESENCE_REMOTE = "remote";
export const WL_PRESENCE_OOO = "out_of_office";
export const WL_NOT_AVAILABLE = "not_available";
export type WL_PRESENCE_OOO = typeof WL_PRESENCE_OOO;
export type WL_PRESENCE_REMOTE = typeof WL_PRESENCE_REMOTE;
export type WL_PRESENCE_OFFICE = typeof WL_PRESENCE_OFFICE;
export type WL_NOT_AVAILABLE = typeof WL_NOT_AVAILABLE;
export type IWorkingLocationPresenceType =
  | WL_PRESENCE_OOO
  | WL_PRESENCE_OFFICE
  | WL_PRESENCE_REMOTE
  | WL_NOT_AVAILABLE;
export interface IWorkingLocation {
  location: IWorkingLocationPresenceType;
  policy_type: LeavePolicyType | null;
  office?: Office;
  resource?: IResource;
  user: IUserFull;
}
export type IWorkingLocations = {
  cursor: string;
  days: {
    [idx in string]: IWorkingLocation[];
  };
};

export interface IOfficesSchedules {
  cursor: string;
  days: {
    [dateIdx: string]: number;
  };
  elements: ISchedule[];
  next_week_date: string;
  previous_week_date: string;
  week_date: string;
}

export interface IOfficesSchedulesIndexed extends IOfficesSchedules {
  dates: {
    [dateIdx: string]: ISchedule[];
  };
  dates_user: {
    [userIdx: string]: {
      [dateIdx: string]: ISchedule[];
    };
  };

  users: {
    [id: string]: IUserFull;
  };
}

export type ResGetOfficesSchedulesMe = IOfficesSchedulesIndexed;
export type ResGetOfficesSchedulesPending = IOfficesSchedulesIndexed;
export type ResGetOfficesSchedulesOccupied = IOfficesSchedulesIndexed;

export type INewsItem = {
  id: string;
  office_id: string;
  title: string;
  body: string;
  updated: string;
  is_pinned?: boolean;
};

export type ResGetNews = {
  cursor: string;
  elements: INewsItem[];
};

export type INotification = Notification;
export type INewsAdded = NewsAdded;
export type IScheduleResponded = ScheduleResponded;

export interface IOfficesSchedulesResourceDay {
  resource: IResource;
  schedules: IOfficesSchedulesIndexed;
}

export type ResGetOfficesSchedulesResourceDay = IOfficesSchedulesResourceDay;

export interface IOfficeUsageFull {
  days: {
    [day: string]: WeekDayNames;
  };
  months: {
    start: string;
    end: string;
  };
  dates_type: {
    [day: string]: {
      [type in ResourceType]?: {
        accepted: number;
        free: number;
        occupancy: number;
        total: number;
      };
    };
  };
  totals: {
    [type in ResourceType]?: number;
  };
  week_date: string;
  previous_week_date: string;
  next_week_date: string;
}

export type ResOfficesSchedulesOccupiedUsage = IOfficeUsageFull;

export type ResGetUser = {
  about: string;
  birthdate: string;
  default_organization_id: string;
  default_organization?: Organization | null;
  default_office_id: string | null;
  assigned_office_id: string;
  email: string;
  id: string;
  impersonated_by: string | null;
  is_being_impersonated: boolean;
  just_created: boolean;
  location: string;
  name: string;
  permissions: string[];
  phone: null | string;
  profile_photo_url: string;
  time_zone: string;
  organization_user: IUserFull;
  date_format: DateFormatStyle;
};

export type ResGetUsers = PaginatedResult<IUserFull>;
export type ResGetUserId = IUserFull;

/** A zone the caller manages at one office, from `GET /v1/users/me`. */
export type ManagedZoneRef = {
  zone_id: string;
  office_id: string;
};

/** The calling user, plus every zone they manage through a zone manager
 *  assignment, already expanded to each zone's subtree. Empty for admins
 *  and resource managers unless they also hold zone assignments. */
export type ResGetMe = ResGetUserId & {
  managed_zones: ManagedZoneRef[];
};

export const DATE_FORMAT_STYLE_YMD = "ymd";
export const DATE_FORMAT_STYLE_DMY = "dmy";
export const DATE_FORMAT_STYLE_MDY = "mdy";
export type DATE_FORMAT_STYLE_YMD = typeof DATE_FORMAT_STYLE_YMD;
export type DATE_FORMAT_STYLE_DMY = typeof DATE_FORMAT_STYLE_DMY;
export type DATE_FORMAT_STYLE_MDY = typeof DATE_FORMAT_STYLE_MDY;
export type DateFormatStyle =
  | DATE_FORMAT_STYLE_YMD
  | DATE_FORMAT_STYLE_DMY
  | DATE_FORMAT_STYLE_MDY;
export type UserRequest = {
  name: string;
  phone: string;
  location: string;
  birthdate: string;
  time_zone: string;
  title: string;
  profile_photo_url: string;
  default_office_id: string;
  default_presence_type: PresenceType;
  date_format: DateFormatStyle;
  about: string;
};

export interface IGenericOption {
  value: string;
  label: string;
}

export type ResGetUsersTeam = ITeamData;
export type ResGetTeams = ITeamData;
export type ResGetTeamsWithOptions = ITeamData & {
  byId: { [id: string]: IUserFull };
  options: { value: string; label: string }[];
};
export type ResGetOffices = PaginatedResult<Office>;
export type CoreResGetOffices = PaginatedResult<CoreOffices>;
export type ResGetOffice = Office;

// these are only available from '/v1/offices'
export type CoreOffices = Office & {
  workspace_reservation_period_recurring_bypass: boolean;
  meeting_room_reservation_period_recurring_bypass: boolean;
};

export interface ResGetFormattedOffices {
  byId: {
    [id: string]: CoreOffices;
  };
  office: Office | null;
  officeId: string | null;
  offices: Office[];
}

export type ResGetEnterpriseDirectories = PaginatedResult<Directory>;
export type ResGetEnterpriseConnections = PaginatedResult<Connection>;
export type ResGetConnectionSessions = PaginatedResult<ConnectionSession>;

export type IOrganizationsUsers = ResGetUsers & {
  letters: Record<string, IUserFull[]>;
};

export type IOrganizationsUsersWithOptions = IOrganizationsUsers & {
  byId: { [id: string]: IUserFull };
  options: { value: string; label: string }[];
};

export interface ReportFilter {
  start: Date;
  end: Date;
  officeIds: string[];
  resourceTypes: ResourceType[];
  search: string | null;
  limit?: number;
}

export interface ReportVisitFilter {
  start: Date;
  end: Date;
  officeIds: string[];
  search: string | null;
  limit?: number;
}

export type ResourceTypeWithNumbers = { [type in ResourceType]: number };

export interface ReportUsage {
  used: ResourceTypeWithNumbers;
  total: ResourceTypeWithNumbers;
  open_used: ResourceTypeWithNumbers;
  open_total: ResourceTypeWithNumbers;
  restricted_used: ResourceTypeWithNumbers;
  restricted_total: ResourceTypeWithNumbers;
  reservations_used: ResourceTypeWithNumbers;
  reservations: ResourceTypeWithNumbers;
  presence: ResourceTypeWithNumbers;
}

export interface ReportUsageDate extends ReportUsage {
  date: string;
}

export interface ReportUsageOffice extends ReportUsageDate {
  office: Office;
}

export interface IReportUsages {
  total: ReportUsage | null;
  dates: ReportUsageDate[];
  usages: ReportUsageOffice[];
  offices: {
    [officeId: string]: ReportUsageOffice[];
  };
}

export type ResGetReportUsage = IReportUsages;

export interface IReportReservation {
  date: string;
  office: Office;
  resource: IResource;
  area: IResource | null;
  has_assigned: boolean;
  assigned_teams: string | null;
  user: IUserFull;
  has_resource_presence: boolean;
}

export interface IVisitor {
  office: Office;
  visitor: IUserFull;
  host: IUserFull;
  station: IStation;
  visit: IVisit;
}

export type ResGetVisits = PaginatedResult<IVisitor>;

export type ResGetReportReservations = PaginatedResult<IReportReservation>;

export interface IReportResource {
  office: Office;
  resource: IResource;
  area: IResource | null;
  has_assigned: boolean;
  usage_days: number;
  total_days: number;
  usage: number;
}

export type ResGetReportResources = PaginatedResult<IReportResource>;

export type ResPostHealthScanVaccination = {
  vaccines: IVaccine[];
  doses: number;
  dates: string[];
  lots: string[];
};

export type ResPostHealthScanTest = {
  date: string;
  type: HealthTestType;
  result: HealthTestResult;
};

export type ResGetHealthVaccines = {
  elements: IVaccine[];
  cursor: string;
};

export type ResGetHealthVaccinationsDocument = {
  elements: IVaccinationDocumentPartial[];
  cursor: string;
};

export type ResGetHealthTestsDocument = {
  elements: ITestDocumentPartial[];
  cursor: string;
};

export type ResGetHealthDocuments = {
  elements: IDocumentPartial[];
  cursor: string;
};

export type PostVaccinationsDoc = IVaccinationDocument;
export type PostTestsDoc = ITestDocument;
export type PostOperationalHours = {
  [idx in WeekDayNames]: {
    id?: string;
    is_available: boolean;
    start_time: Date;
    end_time: Date;
  };
};
export type PostHours = IHourPost;
export type ResGetAllHours = PaginatedResult<IHour>;

export type PostGuestCheckin = {
  id?: string | null;
  name: string;
  email: string;
  reason?: string | null;
  comment: string;
  picture: string;
  host_id: string;
  phone?: string;
  invitation_id?: string;
  expected?: string | null;
};

export type PutGuest = PostGuestCheckin & { office_id: string };

export type PostGuestCode = {
  code: string;
};

export type ResGetStation = {
  id: string;
  name: string;
  type: "visitor";
  is_enabled: boolean;
  status: "active" | "inactive";
  organization: Organization;
  office: Office;
  office_id: string;
  area_id: string;
  settings: {
    badges_enabled: boolean;
    printer: string | null;
    phone_number_enabled: boolean;
  };
};

export type ResGetStations = PaginatedResult<ResGetStation>;

export type ResCreateStation = {
  email: string;
  secret: string;
  station: ResGetStation;
  id: string;
  name: string;
  status: string;
  organization: Organization;
  office: Office;
};

export type ResVisitPresence = IVisit;

export type ResVisitGuest = {
  office: Office;
  host: IUserFull;
  visitor: IUserFull;
  station: ResGetStation;
  visit: ResVisitPresence;
};

export type ResGetSignatures = {
  elements: ResGetSignatue[];
  cursor: string;
  total: number;
};

export type ResGetSignatue = {
  id: string;
  form_id: string;
  form_version: string | null;
  form_file_path: string | null;
  form_title: string;
  file_path: string | null;
  pdf_file_path: string | null;
  user_id: string;
  user_signature: string;
  name: string;
  date_signed: string;
  consented: string | null;
  station_id: string | null;
  visit_id: string | null;
  created: string;
  updated: string;
};

export type SignatureFilter = {
  signature_id?: string;
  user_id?: string;
  visit_id?: string;
};

export type PostResourceScheduledPermissions = {
  resource_ids: string[];
  teams?: string[];
  users?: string[];
  is_approvable: boolean;
  since?: string;
  until?: string;
  access_group_type: AssignmentGroupType;
};

export type PostResourceManagerPermissions = {
  resource_ids: string[];
  users?: string[];
  since?: string;
  until?: string;
};

export type PutResourceScheduledPermissions = {
  is_approvable: boolean;
  since?: string | null;
  until?: string | null;
  anchor_days?: WeekDayNames[];
};

export type GetResourceSchedulePermission = {
  resource_permissions: {
    [resId: string]: IResourcePermission[];
  };
};

export type GetResourceManagerPermission = {
  elements: IUserResourceManaged[];
};

export type ResourcePermissionFilter = {
  hierarchy?: boolean;
  includePast?: boolean;
};

export type IWifiConnection = WifiConnection;

export type IWifiAppInfo = WifiAppInfo;

export type IWifiAppDownload = WifiAppDownload;

export type IWifiAppDownloadType = WifiAppDownloadType;

export type ResGetWifiConnections = {
  elements: WifiConnection[];
  cursor: string;
  total: number;
};

export type PostWifiConnection = {
  ssid?: string;
  office_ids: string[];
  subnet_masks?: string[];
  enforce_subnet_validation?: boolean;
  public_ips?: string[];
};

export type IMediaUri = {
  id: string;
  uploaded: boolean;
  uri: string;
  variants: ResourceImageVariants;
};

export type IMediaUploadResult = {
  id: string;
  type: "image";
  upload_id: string;
  uri: string;
  weight: number;
  created: string;
  updated: string;
  variants: ResourceImageVariants;
};

export type IMediaUploadResponse = {
  errors: string[];
  messages: string[];
  result: {
    filename: string;
    id: string;
    meta: {
      media_type: "image";
      organization_id: string;
      resource_id: string;
      user_id: string;
    };
    requireSignedURLs: boolean;
    uploaded: string;
    variants: string[];
  };
  success: boolean;
};

export type IApplication = Application;

export type ResGetApplications = {
  elements: IApplication[];
  cursor: string;
  total: number;
};

export type PostApplication = {
  name: string;
  description?: string | null;
  redirect_uris: string[];
  is_enabled?: boolean;
  generate_key?: boolean;
};

export interface PageSection {
  id: string;
  name: string;
  description: string;
  parent_id?: string | null;
  active: boolean;
  position: number;
  image_url?: string | null;
  organization_id?: string | null;
  division_id?: string | null;
  office_id?: string | null;
  created_at: string;
  updated_at: string;
  pages?: PagePost[];
}

export interface SectionsPages {
  id: string;
  page_id: string;
  sort_order: number;
  created?: boolean;
  updated?: boolean;
}

export interface PagesSections {
  id: string;
  section_id: string;
  sort_order: number;
  created?: boolean;
  updated?: boolean;
}

export interface PagePost {
  id: string;
  title: string;
  content: string;
  section_id: string;
  status: string;
  sections?: SectionsPages[];
}

export type IApplicationWithKeys = ApplicationWithKeys;

export * from "./tags";

// Tessa

export interface TessaChatElement {
  id: string;
  created: string;
  last_message: string;
  title: string;
}

export interface TessaChatListsResponse {
  cursor: string;
  elements: TessaChatElement[];
}

export enum TessaMessageTypes {
  text = "text",
  tool_get_current_date_time = "tool-get_current_date_time"
}

export type TessaMessageRole = "tool" | "assistant" | "user";
export type TessaMessageType = keyof typeof TessaMessageTypes;

export interface TessaChatMessage {
  id: string;
  role: TessaMessageRole;
  content: string;
  created: string;
  tool_name?: string;
  tool_id?: string;
  state?: {
    data?: any;
    state?: any;
  };
}

export interface TessaChatMessagesResponse {
  cursor: string;
  elements: TessaChatMessage[];
}

export interface AiChatPart {
  type: TessaMessageType;
  toolCallId: string; // This is from live messages
  state: string;
  output?: string;
}

export interface AiChatTextPart extends AiChatPart {
  text: string;
}

export interface AiChatToolGetDateTimePart extends AiChatPart {
  output: string;
}

export interface AiChatToolGetUsersWorkingLocationsPart extends AiChatPart {
  input: {
    date: string;
    emails: string[];
  };
  output: string;
}

export interface AiChatToolBuildWorkplaceRequestPart extends AiChatPart {
  output: string;
  input: {
    description: string;
    office_id: string;
    resource_id: string;
    schedule_id: string;
    title: string;
    type_id_or_name: string;
  };
}

export interface AiChatToolBuildWorkplaceRequestOutput {
  description: string;
  office_id: string;
  resource_id: string;
  schedule_id: string;
  title: string;
  status: string;
  type_id: string;
}

export type AiChatToolGetUsersWorkingLocationsOutput = {
  _metadata?: {
    users_by_id?: Record<string, IUserFull>;
    schedules_by_id?: Record<string, ISchedule>;
  };
  users_locations?: Record<
    string,
    {
      location: string;
      office_id: string;
      schedule_ids: string[];
      user_email: string;
    }
  >;
};

export interface AiChatToolShowReservationInfoPart extends AiChatPart {
  output: string;
  input: {};
}

export interface AiChatToolShowReservationInfoOutput {
  _metadata?: {
    resources_by_id?: Record<string, IResource>;
    users_by_id?: Record<string, IUserFull>;
    /** Each recurring reservation's dates and the ones it can't be booked on, by its position in reservations */
    recurring_by_reservation?: Record<
      string,
      {
        dates: string[];
        clashes: {
          date: string;
          reason: string;
          alternative_resource_ids: string[];
        }[];
        has_more: boolean;
      }
    >;
  };
  reservations?: {
    title?: string;
    organizer_user_id?: string;
    description?: string;
    resource_id: string;
    start: string;
    end: string;
    user_ids?: string[];
    recurring?: {
      start_date?: string;
      end_date?: string;
      rrule: string;
    };
    workplace_request?: {
      description: string;
      office_id: string;
      response_type: string;
      summary: string;
      title: string;
      type_id: string;
    };
    online_meeting_provider?: string;
  }[];
}

export interface AiChatToolShowReleaseReservationsPart extends AiChatPart {
  output: string;
  input: Record<string, never>;
}

/** The user's reservations to release for a day, which they confirm in the card. */
export interface AiChatToolShowReleaseReservationsOutput {
  summary?: string;
  /** yyyy-MM-dd */
  date?: string;
  schedule_ids?: string[];
  _metadata?: {
    schedules_by_id?: Record<string, ISchedule>;
    /** The user's dedicated desks, which the card offers to release that day. */
    dedicated_resources?: IResource[];
  };
}

export interface AiChatToolShowReservationChangePart extends AiChatPart {
  output: string;
  input: Record<string, never>;
}

/** An existing reservation moved to another resource and/or time, which the user confirms. */
export interface AiChatToolShowReservationChangeOutput {
  summary?: string;
  schedule_id: string;
  /** The new resource; the reservation keeps its resource when missing. */
  resource_id?: string;
  /** New times in the office's time, e.g. 2026-10-02T09:00:00; the times stay when missing. */
  start?: string;
  end?: string;
  _metadata?: {
    schedules_by_id?: Record<string, ISchedule>;
    resources_by_id?: Record<string, IResource>;
    /** Meeting room equipment as labels, e.g. "Projector", "2 × Monitor". */
    amenities_by_resource_id?: Record<string, string[]>;
  };
}

export interface AiChatToolShowReportPart extends AiChatPart {
  output: string;
  input: {};
}

/** The structured `report_result` Tessa replies with: the client runs `query` and draws it. */
export interface AiChatToolShowReportOutput {
  summary?: string;
  query?: {
    measure: string;
    breakdown?: string[];
    filters?: { dimension: string; in: string[] }[];
    period: {
      preset: string;
      from?: string;
      to?: string;
      grain?: string;
    };
    compare?: string;
  };
  /** A second measure drawn beside the query's ("filed vs resolved"). */
  with?: string;
  /** chart (default), table, or map for a floor plan. */
  form?: string;
}

/** A workplace request as tessa shows it, with its activity when a single request is shown. */
export interface TessaWorkplaceRequest {
  id: string;
  number: number;
  title: string;
  type: string;
  status: WorkplaceRequestStatus;
  sub_status?: string;
  priority: "low" | "medium" | "high";
  office?: string;
  location?: string;
  floor?: string;
  submitted_by?: string;
  assigned_to?: string;
  created: string;
  updated: string;
  resolved?: string;
  url: string;
  activity?: {
    at: string;
    by?: string;
    comment?: string;
    change?: string;
  }[];
}

export interface AiChatToolShowWorkplaceRequestsPart extends AiChatPart {
  output: string;
  input: Record<string, unknown>;
}

/** The structured `workplace_requests_result` Tessa replies with to show existing requests. */
export interface AiChatToolShowWorkplaceRequestsOutput {
  summary?: string;
  request_ids?: string[];
  _metadata?: {
    workplace_requests_by_id?: Record<string, TessaWorkplaceRequest>;
  };
}

export interface AiChatToolShowVisitInfoPart extends AiChatPart {
  output: string;
  input: {};
}

/** The `visit_result` Tessa replies with: a visitor invitation the user reviews and sends. */
export interface AiChatToolShowVisitInfoOutput {
  summary?: string;
  visitor_name?: string;
  visitor_email?: string;
  company?: string;
  office_id?: string;
  /** Wall clock time in the office, e.g. 2026-10-08T10:00:00 */
  expected?: string;
  visitor_type_id?: string;
  host_id?: string;
}

export interface AiChatToolShowVisitsPart extends AiChatPart {
  output: string;
  input: {};
}

/** The `visits_result` Tessa replies with: the client loads the visits of that day. */
export interface AiChatToolShowVisitsOutput {
  summary?: string;
  date?: string;
  office_id?: string;
  /** Only these visits when set */
  visit_ids?: string[];
}

export type TessaAiChatPart = AiChatTextPart | AiChatToolGetDateTimePart;
